import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Avatar,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
  Tooltip,
  Typography,
  Switch,
} from "@material-ui/core";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import SearchIcon from "@material-ui/icons/Search";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import GreetingModal from "../../components/GreetingModal";
import EditorDeImagem from "../../components/EditorDeImagem";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getBackendUrl } from "../../config";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { poderDeAdmin, ehOperador } from "../../utils/perfil";


const LIMITE = 1000;

const useStyles = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";

  return {
    wrapper: {
      padding: theme.spacing(1, 1, 4),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(3),
    },
    card: {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 16,
      boxShadow: escuro
        ? "0 4px 6px -1px rgba(0,0,0,0.25), 0 2px 4px -1px rgba(0,0,0,0.15)"
        : "0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)",
      overflow: "hidden",
    },
    cardHeader: {
      display: "flex",
      alignItems: "center",
      gap: 14,
      padding: "18px 24px",
      borderBottom: `1px solid ${theme.palette.divider}`,
      background: escuro
        ? "linear-gradient(180deg, rgba(30,152,85,0.10) 0%, rgba(0,0,0,0) 100%)"
        : "linear-gradient(180deg, #f0fdf4 0%, rgba(255,255,255,0) 100%)",
    },
    cardHeaderTitle: {
      fontWeight: 700,
      fontSize: "1.05rem",
      color: theme.palette.text.primary,
      letterSpacing: "-0.01em",
    },
    cardHeaderSubtitle: {
      fontSize: "0.82rem",
      color: theme.palette.text.secondary,
      marginTop: 2,
    },
    avatar: {
      width: 46,
      height: 46,
      fontWeight: 700,
      backgroundColor: escuro ? "rgba(30,152,85,0.18)" : "#e7f6ed",
      color: "#1E9855",
    },
    corpo: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 320px",
      gap: 28,
      padding: 24,
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr",
      },
    },
    campo: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
        backgroundColor: escuro ? "rgba(255,255,255,0.03)" : "#fafafa",
        alignItems: "flex-start",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: escuro ? "rgba(255,255,255,0.10)" : "#e5e7eb",
      },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#1E9855",
      },
      "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#1E9855 !important",
        borderWidth: 2,
      },
      "& .MuiInputLabel-outlined.Mui-focused": {
        color: "#1E9855",
        fontWeight: 600,
      },
    },
    linhaRodape: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 12,
      marginTop: 16,
    },
    toggleBox: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 14px 6px 6px",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 12,
      backgroundColor: escuro ? "rgba(255,255,255,0.02)" : "#fafafa",
    },
    toggleTexto: {
      fontSize: "0.83rem",
      color: theme.palette.text.secondary,
      lineHeight: 1.3,
      maxWidth: 260,
    },
    salvarBtn: {
      borderRadius: 10,
      fontWeight: 700,
      textTransform: "none",
      padding: "8px 28px",
      fontSize: "0.88rem",
      backgroundColor: "#1E9855",
      color: "#fff",
      boxShadow: "0 4px 14px rgba(30,152,85,0.30)",
      "&:hover": { backgroundColor: "#17803f" },
      "&.Mui-disabled": {
        backgroundColor: escuro ? "rgba(255,255,255,0.08)" : "#e5e7eb",
        color: theme.palette.text.disabled,
        boxShadow: "none",
      },
    },

    previaTitulo: {
      fontSize: "0.72rem",
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      marginBottom: 10,
    },
    previaTela: {
      borderRadius: 14,
      padding: 16,
      minHeight: 190,
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-end",
      border: `1px solid ${theme.palette.divider}`,

      backgroundColor: escuro ? "#131917" : "#e9f1e9",
      backgroundImage: escuro
        ? "radial-gradient(rgba(255,255,255,0.035) 1px, transparent 1px)"
        : "radial-gradient(rgba(0,0,0,0.045) 1px, transparent 1px)",
      backgroundSize: "14px 14px",


      contain: "paint",
    },
    balao: {
      display: "flex",
      flexDirection: "column",
      alignSelf: "flex-end",
      maxWidth: "92%",
      backgroundColor: escuro ? "#075E54" : "#d9fdd3",
      color: escuro ? "#e9edef" : "#111b21",
      borderRadius: "12px 12px 2px 12px",
      padding: "9px 12px 6px",
      fontSize: "0.84rem",
      lineHeight: 1.45,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
    },
    // Com imagem, o balão ocupa uma largura fixa e a imagem fica com a mesma
    // margem dos dois lados (antes o minWidth de 260px fazia a imagem vazar
    // do balão pela direita e a prévia ficava torta).
    balaoComImagem: {
      width: "88%",
      padding: "4px 4px 6px",
    },
    balaoImagem: {
      width: "100%",
      height: "auto",
      maxHeight: 320,
      objectFit: "cover",
      borderRadius: 9,
      display: "block",
    },
    balaoLegenda: {
      padding: "6px 7px 0",
    },

    areaImagem: {
      marginTop: 14,
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
    },
    miniatura: {
      width: 62,
      height: 62,
      borderRadius: 10,
      objectFit: "cover",
      border: `1px solid ${theme.palette.divider}`,
      display: "block",
    },
    miniaturaBox: {
      position: "relative",
      width: 62,
      height: 62,
      borderRadius: 10,
      overflow: "hidden",
      cursor: "pointer",
      flexShrink: 0,

      "& $lapis": { opacity: 0 },
      "&:hover $lapis": { opacity: 1 },
    },
    lapis: {
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(0,0,0,0.55)",
      color: "#fff",
      transition: "opacity 0.15s ease",
    },
    miniaturaVazia: {
      width: 62,
      height: 62,
      borderRadius: 10,
      border: `1px dashed ${theme.palette.divider}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: theme.palette.text.disabled,
    },
    botaoImagem: {
      textTransform: "none",
      borderRadius: 10,
      fontWeight: 600,
    },
    dicaImagem: {
      fontSize: "0.72rem",
      color: theme.palette.text.secondary,
      width: "100%",
    },

    balaoVazio: {
      alignSelf: "center",
      color: theme.palette.text.secondary,
      fontSize: "0.8rem",
      textAlign: "center",
      fontStyle: "italic",
    },
    balaoRodape: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 4,
      marginTop: 2,
      fontSize: "0.66rem",
      opacity: 0.7,
    },

    secaoTitulo: {
      fontWeight: 700,
      fontSize: "1.05rem",
      color: theme.palette.text.primary,
    },
    secaoSubtitulo: {
      fontSize: "0.82rem",
      color: theme.palette.text.secondary,
      marginTop: 2,
    },
    barraBusca: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 16,
      flexWrap: "wrap",
      padding: "18px 24px",
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    busca: {
      minWidth: 240,
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        backgroundColor: escuro ? "rgba(255,255,255,0.03)" : "#fafafa",
      },
    },
    grade: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
      gap: 16,
      padding: 24,
    },
    itemUsuario: {
      display: "flex",
      flexDirection: "column",
      gap: 10,
      padding: 16,
      borderRadius: 14,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: escuro ? "rgba(255,255,255,0.02)" : "#fcfcfd",

      "&:hover": {
        borderColor: "#1E9855",
      },
    },
    itemTopo: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    itemNome: {
      fontWeight: 700,
      fontSize: "0.95rem",
      color: theme.palette.text.primary,
      lineHeight: 1.2,
    },
    itemEmail: {
      fontSize: "0.76rem",
      color: theme.palette.text.secondary,
    },
    itemMensagem: {
      fontSize: "0.82rem",
      color: theme.palette.text.secondary,
      lineHeight: 1.45,
      display: "-webkit-box",
      WebkitLineClamp: 3,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      minHeight: 54,
      padding: "10px 12px",
      borderRadius: 10,
      backgroundColor: escuro ? "rgba(255,255,255,0.03)" : "#f3f4f6",
    },
    itemSemMensagem: {
      fontStyle: "italic",
      opacity: 0.75,
    },
    itemImagem: {
      width: "100%",
      height: 110,
      objectFit: "cover",
      borderRadius: 10,
      border: `1px solid ${theme.palette.divider}`,
      display: "block",
      backgroundColor: escuro ? "rgba(255,255,255,0.03)" : "#f3f4f6",
    },
    itemRodape: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
    },
    chipAtiva: {
      height: 22,
      fontSize: "0.7rem",
      fontWeight: 700,
      backgroundColor: escuro ? "rgba(30,152,85,0.18)" : "#e7f6ed",
      color: "#1E9855",
    },
    chipInativa: {
      height: 22,
      fontSize: "0.7rem",
      fontWeight: 700,
      backgroundColor: escuro ? "rgba(255,255,255,0.06)" : "#f3f4f6",
      color: theme.palette.text.secondary,
    },
    chipAdmin: {
      height: 22,
      fontSize: "0.7rem",
      fontWeight: 700,
      backgroundColor: escuro ? "rgba(96,165,250,0.16)" : "#eff6ff",
      color: escuro ? "#93c5fd" : "#2563eb",
    },
    rodapeGrade: {
      display: "flex",
      justifyContent: "center",
      padding: "0 24px 24px",
    },
    carregarMais: {
      textTransform: "none",
      borderRadius: 10,
      fontWeight: 600,
    },
    vazio: {
      padding: 40,
      textAlign: "center",
      color: theme.palette.text.secondary,
      fontSize: "0.88rem",
    },
  };
});

const iniciais = (nome) => (nome ? nome.trim().charAt(0).toUpperCase() : "?");

const avatarSrc = (usuario) =>
  usuario && usuario.profileImage
    ? `${getBackendUrl()}public/${usuario.profileImage}`
    : undefined;

const propsDaImagem = { loading: "lazy", decoding: "async" };






const CartaoAtendente = memo(({ usuario, classes, onEditar }) => {
  const temMensagem = !!(usuario.greetingMessage || "").trim();
  const cadastrada = temMensagem || !!usuario.greetingMediaUrl;
  const enviando = cadastrada && usuario.sendGreetingOnAccept !== false;

  return (
    <div className={classes.itemUsuario}>
      <div className={classes.itemTopo}>
        <Avatar
          className={classes.avatar}
        >
          {iniciais(usuario.name)}
        </Avatar>
        <div style={{ minWidth: 0, flex: 1 }}>
          <Typography className={classes.itemNome} noWrap>
            {usuario.name}
          </Typography>
          <Typography className={classes.itemEmail} noWrap>
            {usuario.email}
          </Typography>
        </div>
        <Tooltip title="Editar saudação">
          <IconButton size="small" onClick={() => onEditar(usuario)}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      <div
        className={`${classes.itemMensagem} ${
          temMensagem ? "" : classes.itemSemMensagem
        }`}
      >
        {temMensagem
          ? usuario.greetingMessage
          : usuario.greetingMediaUrl
          ? "Só imagem, sem texto."
          : "Nenhuma saudação configurada."}
      </div>

      <div className={classes.itemRodape}>
        <Chip
          size="small"
          label={!cadastrada ? "Sem saudação" : enviando ? "Automática" : "Só manual"}
          className={enviando ? classes.chipAtiva : classes.chipInativa}
        />
        {usuario.profile === "admin" && (
          <Chip
            size="small"
            label="Administrador"
            className={classes.chipAdmin}
          />
        )}
        {usuario.profile === "operador" && (
          <Chip
            size="small"
            label="Operador"
            className={classes.chipAdmin}
          />
        )}
      </div>
    </div>
  );
});

const Saudacoes = () => {
  const classes = useStyles();
  const { user: logado } = useContext(AuthContext);
  // Admin e operador gerenciam as saudacoes da equipe. O operador nao tem
  // saudacao propria (recebe o cliente no meio da formalizacao).
  const ehAdmin = poderDeAdmin(logado?.profile);
  const souOperador = ehOperador(logado?.profile);

  const [mensagem, setMensagem] = useState("");
  const [ativa, setAtiva] = useState(true);
  const [original, setOriginal] = useState({ mensagem: "", ativa: true });
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const [imagem, setImagem] = useState("");
  const [enviandoImagem, setEnviandoImagem] = useState(false);
  const [paraEditar, setParaEditar] = useState(null);
  const [carregandoEdicao, setCarregandoEdicao] = useState(false);

  const [usuarios, setUsuarios] = useState([]);
  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [carregandoEquipe, setCarregandoEquipe] = useState(false);
  const [temMais, setTemMais] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [emEdicao, setEmEdicao] = useState(null);


  const requisicaoAtual = useRef(0);

  // O admin tambem tem a propria saudacao. Antes esta tela pulava a busca
  // quando quem entrava era admin, e por isso ele nunca via o cartao dele —
  // so o da equipe. Continua sendo opcional: em branco, nada e enviado.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/greeting");
        const msg = data.greetingMessage || "";
        const on = data.sendGreetingOnAccept !== false;
        setMensagem(msg);
        setAtiva(on);
        setImagem(data.greetingMediaUrl || "");
        setOriginal({ mensagem: msg, ativa: on });
      } catch (err) {
        toastError(err);
      } finally {
        setCarregando(false);
      }
    })();
  }, [ehAdmin]);




  useEffect(() => {
    const timer = setTimeout(() => setBuscaAplicada(busca.trim()), 400);
    return () => clearTimeout(timer);
  }, [busca]);




  const buscarPagina = useCallback(
    async (numeroPagina, acumular) => {
      if (!ehAdmin) return;

      const idDaVez = requisicaoAtual.current + 1;
      requisicaoAtual.current = idDaVez;

      setCarregandoEquipe(true);
      try {
        const { data } = await api.get("/users/", {
          params: { searchParam: buscaAplicada, pageNumber: numeroPagina },
        });


        if (requisicaoAtual.current !== idDaVez) return;

        // Admin ve e edita vendedores e operadores; operador so os vendedores.
        const lista = (data.users || []).filter(
          (u) => u.profile === "user" || (!souOperador && u.profile === "operador")
        );

        setUsuarios((anteriores) =>
          acumular ? [...anteriores, ...lista] : lista
        );
        setTemMais(!!data.hasMore);
        setPagina(numeroPagina);
      } catch (err) {
        if (requisicaoAtual.current === idDaVez) toastError(err);
      } finally {
        if (requisicaoAtual.current === idDaVez) setCarregandoEquipe(false);
      }
    },
    [buscaAplicada, ehAdmin]
  );

  useEffect(() => {
    buscarPagina(1, false);
  }, [buscarPagina]);

  const recarregarEquipe = useCallback(() => {
    buscarPagina(1, false);
  }, [buscarPagina]);

  const abrirEdicao = useCallback((usuario) => setEmEdicao(usuario), []);
  const fecharEdicao = useCallback(() => setEmEdicao(null), []);

  const alterado = mensagem !== original.mensagem || ativa !== original.ativa;

  const salvar = async () => {
    setSalvando(true);
    try {
      await api.put("/me/greeting", {
        greetingMessage: mensagem,
        sendGreetingOnAccept: ativa,
      });
      setOriginal({ mensagem, ativa });
      toast.success("Sua saudação foi salva!");
    } catch (err) {
      toastError(err);
    } finally {
      setSalvando(false);
    }
  };

  const urlDaImagem = imagem ? `${getBackendUrl()}public/${imagem}` : "";




  const escolherImagem = (e) => {
    const arquivo = e.target.files?.[0];
    e.target.value = "";
    if (!arquivo) return;

    if (!/^image\/(jpe?g|png|webp)$/i.test(arquivo.type)) {
      toast.error("Use uma imagem JPG, PNG ou WEBP.");
      return;
    }

    if (arquivo.size > 15 * 1024 * 1024) {
      toast.error("A imagem precisa ter no máximo 15 MB.");
      return;
    }

    setParaEditar(arquivo);
  };

  const editarImagemAtual = async () => {
    if (!imagem || carregandoEdicao) return;

    setCarregandoEdicao(true);
    try {
      const { data, headers } = await api.get(`/public/${imagem}`, {
        responseType: "blob",
      });

      const tipo = headers["content-type"] || "image/jpeg";
      setParaEditar(new File([data], imagem, { type: tipo }));
    } catch (err) {
      toastError(err);
    } finally {
      setCarregandoEdicao(false);
    }
  };

  const enviarImagem = async (arquivoFinal) => {
    setParaEditar(null);
    setEnviandoImagem(true);
    try {
      const corpo = new FormData();
      corpo.append("greetingImage", arquivoFinal);

      const { data } = await api.post("/me/greeting/image", corpo, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setImagem(data.greetingMediaUrl || "");
      toast.success("Imagem da saudação atualizada!");
    } catch (err) {
      toastError(err);
    } finally {
      setEnviandoImagem(false);
    }
  };

  const removerImagem = async () => {
    setEnviandoImagem(true);
    try {
      const { data } = await api.delete("/me/greeting/image");
      setImagem(data.greetingMediaUrl || "");
      toast.success("Imagem removida.");
    } catch (err) {
      toastError(err);
    } finally {
      setEnviandoImagem(false);
    }
  };

  const agora = new Date().toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });




  const grade = useMemo(
    () =>
      usuarios.map((u) => (
        <CartaoAtendente
          key={u.id}
          usuario={u}
          classes={classes}
          onEditar={abrirEdicao}
        />
      )),
    [usuarios, classes, abrirEdicao]
  );

  return (
    <MainContainer>
      <GreetingModal
        open={!!emEdicao}
        usuario={emEdicao}
        onClose={fecharEdicao}
        onSaved={recarregarEquipe}
      />

      <EditorDeImagem
        open={!!paraEditar}
        arquivo={paraEditar}
        onCancel={() => setParaEditar(null)}
        onConfirmar={enviarImagem}
      />

      <div className={classes.wrapper}>
        {}
        {!!logado?.id && (
        <div className={classes.card}>
          <div className={classes.cardHeader}>
            <Avatar
              className={classes.avatar}
              src={avatarSrc(logado)}
              imgProps={propsDaImagem}
            >
              {iniciais(logado?.name)}
            </Avatar>
            <div>
              <Typography className={classes.cardHeaderTitle}>
                Minha saudação
              </Typography>
              <Typography className={classes.cardHeaderSubtitle}>
                Sai sozinha na primeira vez que o cliente é transferido para
                alguém. Nas próximas transferências, envie pelo "+" → "Saudação"
                dentro do atendimento.
                {logado?.profile === "admin"
                  ? " Para quem é administrador é opcional: deixando em branco, nada é enviado."
                  : ""}
              </Typography>
            </div>
          </div>

          {carregando ? (
            <div className={classes.vazio}>
              <CircularProgress size={26} style={{ color: "#1E9855" }} />
            </div>
          ) : (
            <div className={classes.corpo}>
              <div>
                <TextField
                  className={classes.campo}
                  label="Mensagem de apresentação"
                  placeholder={`Olá! Tudo bem? Me chamo ${
                    logado?.name || "..."
                  } e vou dar continuidade ao seu atendimento 😊`}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={7}
                  value={mensagem}
                  onChange={(e) =>
                    setMensagem(e.target.value.slice(0, LIMITE))
                  }
                  helperText={`${mensagem.length}/${LIMITE} caracteres · deixe em branco para não enviar nada`}
                  inputProps={{ style: { lineHeight: 1.5 } }}
                />

                {}
                <div className={classes.areaImagem}>
                  <input
                    id="input-imagem-saudacao"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    style={{ display: "none" }}
                    onChange={escolherImagem}
                  />

                  {urlDaImagem ? (
                    <Tooltip title="Editar enquadramento">
                      <div
                        className={classes.miniaturaBox}
                        onClick={editarImagemAtual}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") editarImagemAtual();
                        }}
                      >
                        <img
                          className={classes.miniatura}
                          src={urlDaImagem}
                          alt="Imagem da saudação"
                          loading="lazy"
                          decoding="async"
                        />
                        <span className={classes.lapis}>
                          {carregandoEdicao ? (
                            <CircularProgress
                              size={18}
                              style={{ color: "#fff" }}
                            />
                          ) : (
                            <EditOutlinedIcon fontSize="small" />
                          )}
                        </span>
                      </div>
                    </Tooltip>
                  ) : (
                    <label
                      htmlFor="input-imagem-saudacao"
                      className={classes.miniaturaVazia}
                      style={{ cursor: "pointer" }}
                    >
                      <ImageOutlinedIcon />
                    </label>
                  )}

                  <label htmlFor="input-imagem-saudacao">
                    <Button
                      component="span"
                      variant="outlined"
                      size="small"
                      className={classes.botaoImagem}
                      disabled={enviandoImagem}
                      startIcon={
                        enviandoImagem ? (
                          <CircularProgress size={14} />
                        ) : (
                          <ImageOutlinedIcon fontSize="small" />
                        )
                      }
                    >
                      {urlDaImagem ? "Trocar imagem" : "Adicionar imagem"}
                    </Button>
                  </label>

                  {urlDaImagem && (
                    <Button
                      size="small"
                      className={classes.botaoImagem}
                      disabled={enviandoImagem}
                      onClick={removerImagem}
                      startIcon={<DeleteOutlineIcon fontSize="small" />}
                    >
                      Remover
                    </Button>
                  )}

                  <span className={classes.dicaImagem}>
                    Opcional. A imagem vai junto com a mensagem, como legenda.
                    Dá para enquadrar, girar e ajustar o zoom antes de salvar.
                  </span>
                </div>

                <div className={classes.linhaRodape}>
                  <div className={classes.toggleBox}>
                    <Switch
                      checked={ativa}
                      onChange={(e) => setAtiva(e.target.checked)}
                      color="primary"
                      size="small"
                    />
                    <span className={classes.toggleTexto}>
                      {ativa
                        ? "Envio automático ligado (1ª transferência)"
                        : "Envio automático desligado (só pelo \"+\")"}
                    </span>
                  </div>

                  <Button
                    variant="contained"
                    className={classes.salvarBtn}
                    disabled={!alterado || salvando}
                    onClick={salvar}
                  >
                    {salvando ? "Salvando..." : "Salvar saudação"}
                  </Button>
                </div>
              </div>

              <div>
                <Typography className={classes.previaTitulo}>
                  Prévia para o cliente
                </Typography>
                <div className={classes.previaTela}>
                  {mensagem.trim() || urlDaImagem ? (
                    <div
                      className={`${classes.balao} ${urlDaImagem ? classes.balaoComImagem : ""}`}
                    >
                      {urlDaImagem && (
                        <img
                          className={classes.balaoImagem}
                          src={urlDaImagem}
                          alt="Imagem da saudação"
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                      {urlDaImagem ? (
                        mensagem.trim() && <div className={classes.balaoLegenda}>{mensagem}</div>
                      ) : (
                        mensagem
                      )}
                      <div
                        className={classes.balaoRodape}
                        style={urlDaImagem ? { padding: "0 6px" } : undefined}
                      >
                        <span>{agora}</span>
                        <DoneAllIcon style={{ fontSize: 13 }} />
                      </div>
                    </div>
                  ) : (
                    <span className={classes.balaoVazio}>
                      Sem mensagem configurada.
                      <br />Cadastre um texto ou imagem para poder enviar.
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {}
        {ehAdmin && (
          <div className={classes.card}>
            <div className={classes.barraBusca}>
              <div>
                <Typography className={classes.secaoTitulo}>
                  Saudações da equipe
                </Typography>
                <Typography className={classes.secaoSubtitulo}>
                  A mensagem que cada atendente envia ao receber um cliente
                  transferido. Clique no lápis para editar.
                </Typography>
              </div>
              <TextField
                className={classes.busca}
                variant="outlined"
                size="small"
                placeholder="Buscar login..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        style={{ fontSize: 19, opacity: 0.6 }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: carregandoEquipe ? (
                    <InputAdornment position="end">
                      <CircularProgress size={15} style={{ color: "#1E9855" }} />
                    </InputAdornment>
                  ) : null,
                }}
              />
            </div>

            {carregandoEquipe && !usuarios.length ? (
              <div className={classes.vazio}>
                <CircularProgress size={26} style={{ color: "#1E9855" }} />
              </div>
            ) : !usuarios.length ? (
              <div className={classes.vazio}>
                <PersonOutlineIcon
                  style={{ fontSize: 34, opacity: 0.4, display: "block", margin: "0 auto 8px" }}
                />
                Nenhum atendente cadastrado.
              </div>
            ) : (
              <>
                <div className={classes.grade}>{grade}</div>

                {temMais && (
                  <div className={classes.rodapeGrade}>
                    <Button
                      variant="outlined"
                      className={classes.carregarMais}
                      disabled={carregandoEquipe}
                      onClick={() => buscarPagina(pagina + 1, true)}
                    >
                      {carregandoEquipe ? "Carregando..." : "Carregar mais"}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </MainContainer>
  );
};

export default Saudacoes;
